import { Expandable } from "@/components/Expandable";
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { ChevronLeft, ChevronRight, CalendarDays, CalendarClock, Compass } from "lucide-react";
import SessionDialog from "@/components/SessionDialog";

const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const DOW = ["S","M","T","W","T","F","S"];

const formatDay = (iso) => {
    try {
        const [y, m, d] = iso.split("-").map(Number);
        const date = new Date(y, m - 1, d);
        return date.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
    } catch { return iso; }
};

const FILTERS = [
    { id: "all", label: "All", icon: CalendarDays },
    { id: "pas", label: "PAS", icon: CalendarClock },
    { id: "roa", label: "ROA", icon: Compass },
];

const CalendarPage = () => {
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [pas, setPas] = useState([]);
    const [roa, setRoa] = useState([]);
    const { users } = useData();
    const [selected, setSelected] = useState(null);
    const [params, setParams] = useSearchParams();
    const initialType = params.get("type");
    const [filter, setFilter] = useState(initialType === "pas" || initialType === "roa" ? initialType : "all");

    const load = async () => {
        const [p, r] = await Promise.all([
            api.get("/sessions"),
            api.get("/roa/sessions").catch(() => ({ data: [] })),
        ]);
        setPas((p.data || []).map((s) => ({ ...s, _module: "pas" })));
        setRoa((r.data || []).map((s) => ({ ...s, _module: "roa" })));
    };

    useEffect(() => { load(); }, []);

    const setFilterAndUrl = (id) => {
        setFilter(id);
        const next = new URLSearchParams(params);
        if (id === "all") next.delete("type"); else next.set("type", id);
        setParams(next, { replace: true });
    };

    const nameOf = (uid) => users.find((x) => x.user_id === uid)?.name || "—";

    const visible = useMemo(() => {
        if (filter === "pas") return pas;
        if (filter === "roa") return roa;
        return [...pas, ...roa];
    }, [filter, pas, roa]);

    const byDate = useMemo(() => {
        const m = {};
        for (const s of visible) {
            (m[s.scheduled_date] = m[s.scheduled_date] || []).push(s);
        }
        return m;
    }, [visible]);

    const today = new Date().toISOString().slice(0, 10);

    const counts = { all: pas.length + roa.length, pas: pas.length, roa: roa.length };

    return (
        <div className="space-y-6" data-testid="calendar-page">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Year View</div>
                    <h1 className="font-heading text-4xl font-bold text-slate-900 tracking-tight mt-1 flex items-center gap-3">
                        <CalendarDays className="h-7 w-7 text-[#2EB4AC]" />
                        {year} Calendar
                    </h1>
                    <p className="text-slate-500 mt-1">PAS and ROA sessions, side by side</p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={() => setYear(year - 1)} data-testid="calendar-prev-year" className="h-10 w-10 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-slate-600 flex items-center justify-center transition-colors">
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <div className="h-10 px-4 rounded-lg border border-slate-200 flex items-center font-heading font-semibold text-slate-900 tabular-nums">{year}</div>
                    <button onClick={() => setYear(year + 1)} data-testid="calendar-next-year" className="h-10 w-10 rounded-lg border border-slate-200 hover:border-[#2EB4AC] hover:text-[#2EB4AC] text-slate-600 flex items-center justify-center transition-colors">
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>

            {/* Type filter */}
            <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-1.5 inline-flex gap-1">
                {FILTERS.map((f) => {
                    const isActive = filter === f.id;
                    const active = f.id === "roa" ? "bg-indigo-600 text-white" : f.id === "pas" ? "bg-[#2EB4AC] text-white" : "bg-slate-900 text-white";
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFilterAndUrl(f.id)}
                            data-testid={`calendar-filter-${f.id}`}
                            className={`inline-flex items-center gap-2 h-9 px-3.5 rounded-md text-sm font-medium transition-colors ${
                                isActive ? active : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            <f.icon className="h-4 w-4" />
                            {f.label}
                            <span className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold tabular-nums ${
                                isActive ? "bg-white/20 text-white" : "bg-slate-100 text-slate-600"
                            }`}>
                                {counts[f.id]}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                {MONTHS.map((m, idx) => {
                    const first = new Date(year, idx, 1);
                    const startDow = first.getDay();
                    const daysInMonth = new Date(year, idx + 1, 0).getDate();
                    const cells = [];
                    for (let i = 0; i < startDow; i++) cells.push(null);
                    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

                    return (
                        <div key={m} className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
                            <div className="flex items-center justify-between mb-3">
                                <div className="font-heading font-semibold text-slate-900">{m}</div>
                                <div className="text-xs text-slate-500">{year}</div>
                            </div>
                            <div className="grid grid-cols-7 text-[10px] text-slate-500 uppercase tracking-wider font-semibold mb-1">
                                {DOW.map((d, i) => <div key={i} className="text-center py-1">{d}</div>)}
                            </div>
                            <div className="grid grid-cols-7 gap-1">
                                {cells.map((d, i) => {
                                    if (!d) return <div key={i} />;
                                    const dateStr = `${year}-${String(idx+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
                                    const list = byDate[dateStr] || [];
                                    const isToday = dateStr === today;
                                    const hasConducted = list.some(x => x.status === "conducted");
                                    const hasOverdue = list.some(x => x.status === "upcoming" && x.scheduled_date < today);
                                    const hasUpcoming = list.some(x => x.status === "upcoming" && x.scheduled_date >= today);
                                    const hasMissed = list.some(x => x.status === "missed");
                                    const hasPas = list.some(x => x._module === "pas");
                                    const hasRoa = list.some(x => x._module === "roa");

                                    // Dominant status drives the border colour; for an "upcoming-only" day we
                                    // tint by module (teal for PAS-only, indigo for ROA-only, slate for mixed).
                                    let ringClass = "";
                                    let bgClass = "";
                                    let textClass = "text-slate-400";
                                    if (list.length > 0) {
                                        textClass = "text-slate-900";
                                        if (hasMissed) { ringClass = "border-dashed border-red-400"; bgClass = "bg-red-50"; }
                                        else if (hasOverdue) { ringClass = "border-dashed border-yellow-400"; bgClass = "bg-yellow-50"; }
                                        else if (hasConducted) { ringClass = "border-dashed border-green-400"; bgClass = "bg-green-50"; }
                                        else if (hasUpcoming) {
                                            if (hasPas && hasRoa) { ringClass = "border-dashed border-slate-400"; bgClass = "bg-slate-50"; }
                                            else if (hasRoa) { ringClass = "border-dashed border-indigo-400"; bgClass = "bg-indigo-50"; }
                                            else { ringClass = "border-dashed border-teal-400"; bgClass = "bg-teal-50"; }
                                        }
                                    }

                                    // Multi-session badge colour mirrors the dominant module so a glance distinguishes
                                    // a stack of PAS cycles from a stack of ROA touchpoints on the same day.
                                    const badgeColor = hasPas && hasRoa ? "bg-slate-700" : hasRoa ? "bg-indigo-600" : "bg-[#2EB4AC]";

                                    const weekRow = Math.floor(i / 7);
                                    const totalRows = Math.ceil(cells.length / 7);
                                    const popoverBelow = weekRow < totalRows - 2;

                                    return (
                                        <div key={i} className="relative group">
                                            <button
                                                disabled={list.length === 0}
                                                onClick={() => list[0] && setSelected(list[0])}
                                                data-testid={list.length ? `calendar-day-${dateStr}` : undefined}
                                                className={`w-full aspect-square relative rounded-md text-xs font-semibold flex items-center justify-center transition-all ${
                                                    isToday ? "ring-2 ring-offset-1 ring-[#2EB4AC]" : ""
                                                } ${
                                                    list.length
                                                        ? `border-2 ${ringClass} ${bgClass} ${textClass} cursor-pointer hover:scale-[1.08] hover:shadow-sm`
                                                        : `${textClass}`
                                                }`}
                                            >
                                                {d}
                                                {list.length > 1 && (
                                                    <span className={`absolute -top-1 -right-1 h-4 min-w-[16px] px-1 rounded-full text-white text-[9px] font-bold flex items-center justify-center ring-2 ring-white ${badgeColor}`}>
                                                        {list.length}
                                                    </span>
                                                )}
                                                {/* Tiny module corner dot — present even when only 1 session */}
                                                {list.length === 1 && (hasPas || hasRoa) && (
                                                    <span
                                                        className={`absolute bottom-0.5 right-0.5 h-1.5 w-1.5 rounded-full ${
                                                            hasRoa ? "bg-indigo-500" : "bg-[#2EB4AC]"
                                                        }`}
                                                        aria-hidden
                                                    />
                                                )}
                                            </button>

                                            {list.length > 0 && (
                                                <div
                                                    className={`opacity-0 group-hover:opacity-100 transition-opacity duration-150 absolute left-1/2 -translate-x-1/2 z-30 w-64 ${
                                                        popoverBelow ? "top-full mt-2" : "bottom-full mb-2"
                                                    }`}
                                                >
                                                    <div className="bg-white border border-slate-200 rounded-lg shadow-lg p-3 space-y-2 text-left">
                                                        <div className="flex items-center justify-between text-[11px]">
                                                            <span className="font-semibold text-slate-900">{formatDay(dateStr)}</span>
                                                            <span className="text-slate-500">{list.length} session{list.length!==1?"s":""}</span>
                                                        </div>
                                                        <div className="space-y-2 max-h-52 overflow-y-auto scrollbar-thin">
                                                            <Expandable items={list} limit={5} className="block w-full text-[10px] text-[#25908A] font-semibold text-center" moreLabel={(n) => `+ ${n} more…`} testid="calendar-day-more" render={(s) => {
                                                                const isOverdueOne = s.status === "upcoming" && s.scheduled_date < today;
                                                                const statusCfg = {
                                                                    conducted: { cls: "bg-green-100 text-green-800 border-green-200", label: "Conducted" },
                                                                    missed: { cls: "bg-red-100 text-red-800 border-red-200", label: "Missed" },
                                                                    overdue: { cls: "bg-yellow-100 text-yellow-800 border-yellow-200", label: "Overdue" },
                                                                    upcoming: s._module === "roa"
                                                                        ? { cls: "bg-indigo-100 text-indigo-800 border-indigo-200", label: "Upcoming" }
                                                                        : { cls: "bg-blue-100 text-blue-800 border-blue-200", label: "Upcoming" },
                                                                }[isOverdueOne ? "overdue" : s.status] || { cls: "bg-slate-100 text-slate-800 border-slate-200", label: s.status };
                                                                const moduleCfg = s._module === "roa"
                                                                    ? { cls: "bg-indigo-50 text-indigo-700 border-indigo-200", icon: Compass, label: "ROA" }
                                                                    : { cls: "bg-teal-50 text-[#25908A] border-teal-200", icon: CalendarClock, label: "PAS" };
                                                                const MIcon = moduleCfg.icon;
                                                                return (
                                                                    <button
                                                                        key={`${s._module}-${s.id}`}
                                                                        type="button"
                                                                        onClick={(e) => { e.stopPropagation(); setSelected(s); }}
                                                                        data-testid={`calendar-popover-session-${s.id}`}
                                                                        className="w-full text-left border border-slate-100 rounded-md px-2 py-1.5 bg-slate-50/70 hover:bg-white hover:border-slate-300 transition-colors"
                                                                    >
                                                                        <div className="flex items-center justify-between gap-2">
                                                                            <div className="text-xs font-medium text-slate-900 truncate">{nameOf(s.employee_id)}</div>
                                                                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${moduleCfg.cls}`}>
                                                                                <MIcon className="h-2.5 w-2.5" /> {moduleCfg.label}
                                                                            </span>
                                                                        </div>
                                                                        <div className="flex items-center justify-between gap-2 mt-1">
                                                                            <div className="text-[10px] text-slate-500">
                                                                                Cycle #{s.cycle_number}
                                                                                {s.conducted_date ? ` · Conducted ${s.conducted_date}` : ""}
                                                                                {s.submitted_at ? " · Locked" : ""}
                                                                            </div>
                                                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${statusCfg.cls}`}>
                                                                                {statusCfg.label}
                                                                            </span>
                                                                        </div>
                                                                        {s.notes && (
                                                                            <div className="text-[11px] text-slate-700 mt-1 line-clamp-2 italic">“{s.notes}”</div>
                                                                        )}
                                                                    </button>
                                                                );
                                                            }} />
                                                        </div>
                                                        <div className="text-[10px] text-slate-400 text-center border-t border-slate-100 pt-1.5">
                                                            Click a session to open
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Legend */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-slate-500">
                <Legend swatch="border-2 border-dashed border-teal-400 bg-teal-50" label="PAS upcoming" />
                <Legend swatch="border-2 border-dashed border-indigo-400 bg-indigo-50" label="ROA upcoming" />
                <Legend swatch="border-2 border-dashed border-slate-400 bg-slate-50" label="Mixed upcoming" />
                <Legend swatch="border-2 border-dashed border-green-400 bg-green-50" label="Conducted" />
                <Legend swatch="border-2 border-dashed border-yellow-400 bg-yellow-50" label="Overdue" />
                <Legend swatch="border-2 border-dashed border-red-400 bg-red-50" label="Missed" />
                <Legend swatch="ring-2 ring-[#2EB4AC]" label="Today" />
            </div>

            {selected && (
                <SessionDialog
                    session={selected}
                    onClose={() => setSelected(null)}
                    onSaved={() => load()}
                    users={users}
                    module={selected._module || "pas"}
                />
            )}
        </div>
    );
};

const Legend = ({ swatch, label }) => (
    <div className="inline-flex items-center gap-2">
        <span className={`h-4 w-4 rounded ${swatch}`} />
        {label}
    </div>
);

export default CalendarPage;
